type TeamSideNames = {
	home: string
	away: string
}

type TeamNameWord = {
	value: string
	display: string
}

type DifferingWordPair = {
	left: TeamNameWord | null
	right: TeamNameWord | null
}

export function deriveTeamShortNames(
	homeName: string,
	awayName: string,
	homeFallbackCode: string,
	awayFallbackCode: string,
): TeamSideNames {
	const homeWords = tokenizeTeamName(homeName)
	const awayWords = tokenizeTeamName(awayName)
	const homeLetterWords = homeWords.filter(word => hasLetter(word.value))
	const awayLetterWords = awayWords.filter(word => hasLetter(word.value))
	const differingLetterWords = firstDifferingWordPair(homeLetterWords, awayLetterWords)

	if (differingLetterWords?.left && differingLetterWords.right) {
		const homeInitial = differingLetterWords.left.value[0]
		const awayInitial = differingLetterWords.right.value[0]
		if (homeInitial !== awayInitial) {
			return {
				home: homeInitial,
				away: awayInitial,
			}
		}
	}

	const differingWords = firstDifferingWordPair(homeWords, awayWords)
	if (differingWords && (!differingWords.left || !differingWords.right)) {
		return {
			home: differingWords.left?.value || fallbackShortName(homeWords, homeFallbackCode),
			away: differingWords.right?.value || fallbackShortName(awayWords, awayFallbackCode),
		}
	}
	if (
		differingWords &&
		(!hasLetter(differingWords.left?.value || '') || !hasLetter(differingWords.right?.value || ''))
	) {
		const homePrefix = firstLetterWord(homeWords)?.value[0] || homeFallbackCode
		const awayPrefix = firstLetterWord(awayWords)?.value[0] || awayFallbackCode
		return {
			home: `${homePrefix}${differingWords.left?.value || ''}`,
			away: `${awayPrefix}${differingWords.right?.value || ''}`,
		}
	}

	if (differingLetterWords?.left && differingLetterWords.right) {
		const prefixLength = minimumUniquePrefixLength(differingLetterWords.left.value, differingLetterWords.right.value)
		return {
			home: differingLetterWords.left.value.slice(0, prefixLength),
			away: differingLetterWords.right.value.slice(0, prefixLength),
		}
	}

	return {
		home: fallbackShortName(homeWords, homeFallbackCode),
		away: fallbackShortName(awayWords, awayFallbackCode),
	}
}

export function deriveTeamDisplayNames(
	homeName: string | null | undefined,
	awayName: string | null | undefined,
	homeFallbackName: string,
	awayFallbackName: string,
): TeamSideNames {
	const homeDisplayName = homeName?.trim() || homeFallbackName
	const awayDisplayName = awayName?.trim() || awayFallbackName
	const homeWords = tokenizeTeamName(homeDisplayName)
	const awayWords = tokenizeTeamName(awayDisplayName)
	const homeLetterWords = homeWords.filter(word => hasLetter(word.value))
	const awayLetterWords = awayWords.filter(word => hasLetter(word.value))
	const differingLetterWords = firstDifferingWordPair(homeLetterWords, awayLetterWords)

	if (differingLetterWords) {
		return {
			home: displayDifferingWord(differingLetterWords.left, homeWords, homeDisplayName),
			away: displayDifferingWord(differingLetterWords.right, awayWords, awayDisplayName),
		}
	}

	const differingWords = firstDifferingWordPair(homeWords, awayWords)
	if (differingWords) {
		return {
			home: displayDifferingWordWithPrefix(differingWords.left, homeWords, homeDisplayName),
			away: displayDifferingWordWithPrefix(differingWords.right, awayWords, awayDisplayName),
		}
	}

	return {
		home: fallbackDisplayName(homeWords, homeDisplayName),
		away: fallbackDisplayName(awayWords, awayDisplayName),
	}
}

function tokenizeTeamName(name: string): TeamNameWord[] {
	return name
		.trim()
		.split(/\s+/)
		.map(word => {
			const value = word
				.toUpperCase()
				.replace(/^[^A-Z0-9]+|[^A-Z0-9/]+$/g, '')
			const display = word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9/]+$/g, '')
			return {
				value,
				display: display || value,
			}
		})
		.filter(word => word.value)
}

function firstDifferingWordPair(leftWords: TeamNameWord[], rightWords: TeamNameWord[]): DifferingWordPair | null {
	const maxLength = Math.max(leftWords.length, rightWords.length)

	for (let index = 0; index < maxLength; index += 1) {
		const left = leftWords[index] || null
		const right = rightWords[index] || null
		if (!left || !right) {
			if (left && right?.value !== left.value) {
				return { left, right }
			}
			if (right && left?.value !== right.value) {
				return { left, right }
			}
			continue
		}
		if (left.value !== right.value) {
			return { left, right }
		}
	}

	return null
}

function firstLetterWord(words: TeamNameWord[]) {
	return words.find(word => hasLetter(word.value)) || null
}

function hasLetter(value: string) {
	return /[A-Z]/.test(value)
}

function minimumUniquePrefixLength(left: string, right: string) {
	const maxLength = Math.max(left.length, right.length)
	for (let length = 1; length <= maxLength; length += 1) {
		if (left.slice(0, length) !== right.slice(0, length)) {
			return length
		}
	}
	return maxLength
}

function fallbackShortName(words: TeamNameWord[], fallbackCode: string) {
	const firstWord = firstLetterWord(words) || words[0]
	return firstWord?.value || fallbackCode
}

function fallbackDisplayName(words: TeamNameWord[], fallbackName: string) {
	const firstWord = firstLetterWord(words) || words[0]
	return firstWord?.display || fallbackName
}

function displayDifferingWord(word: TeamNameWord | null, allWords: TeamNameWord[], fallbackName: string) {
	return word?.display || fallbackDisplayName(allWords, fallbackName)
}

function displayDifferingWordWithPrefix(word: TeamNameWord | null, allWords: TeamNameWord[], fallbackName: string) {
	const display = displayDifferingWord(word, allWords, fallbackName)

	if (!word?.value || hasLetter(word.value)) {
		return display
	}

	const prefix = firstLetterWord(allWords)?.display
	if (!prefix || prefix === display) {
		return display
	}

	return `${prefix} ${display}`
}
